import actionTypes from 'redux/actions/actionTypes'
import makeUniqueId from 'helpers/makeUniqueId'
import confirmTypes from 'helpers/enums/confirmTypes'
import lessonTypes from 'helpers/enums/lessonTypes'

const defaultInstructions = {
	text: '',
	status: '',
}

const defaultActivePair = {
	id: 0,
	clientId: 0,
	card1: {},
	card2: {},
}

const defaultActiveCard = {
	id: '',
	type: '',
	value: null,
	touched: true,
	error: null,
}

const defaultTextValue = ''

const defaultImageValue = {
	matrix: [-1, 0, 0, -1, 0, 0],
	originalSize: [],
	icon: {},
}

const defaultIcon = {
	id: 0,
	type: '',
	versions: {},
	link: '',
	status: '',
	message: '',
	uploadId: 0,
}

const defaultTimer = {
	value: null,
}

const defaultState = {
	entityUID: '',
	presentationId: '',
	instructions: {
		...defaultInstructions,
	},
	pairs: [],
	isHeaderExpanded: true,
	timer: {
		...defaultTimer,
	},
	activePair: '',
	touched: true,
	isModified: false,
	canReplace: false,
	cancelUrl: '',
	confirms: [],
}

function initializeView(state, action) {
	return {
		...state,
		entityUID: action.mpUID,
	}
}

function toggleHeader(state) {
	return {
		...state,
		isHeaderExpanded: !state.isHeaderExpanded,
	}
}

function instructionsChange(state, action) {
	return {
		...state,
		isModified: true,
		canReplace: false,
		instructions: {
			...state.instructions,
			text: action.value,
		},
	}
}

function initializeSuccess(state, action) {
	return {
		...state,
		presentationId: action.data.presentationId,
		instructions: {
			...state.instructions,
			text: action.data.instructions,
		},
		timer: {
			value: action.data.timer,
		},
		pairs: action.data.pairs.map(
			pair => {
				pair.deleted = false

				pair.card1.touched = false
				if (pair.card1.type === 'image' && pair.card1.value.matrix !== '') {
					pair.card1.value.matrix = JSON.parse(pair.card1.value.matrix)
				}

				pair.card2.touched = false
				if (pair.card2.type === 'image' && pair.card2.value.matrix !== '') {
					pair.card2.value.matrix = JSON.parse(pair.card2.value.matrix)
				}

				pair.clientId = makeUniqueId()
				return pair
			}
		),
		cancelUrl: action.data.cancelUrl,
		canReplace: action.data.pairs.length === 0 || state.canReplace,
	}
}

function saveTimer(state, action) {
	//Adding previous timer value to action in case it needs to be reverted if service fails when saving
	action.previous_timer = state.timer
	return {
		...state,
		isModified: true,
		canReplace: false,
		timer: {
			value: action.value,
		},
	}
}

function deleteTimer(state, action) {
	//Adding previous timer value to action in case it needs to be reverted if service fails when saving
	action.previous_timer = state.timer
	return {
		...state,
		timer: {
			value: null,
		},
		isModified: true,
		canReplace: false,
	}
}

function clearConfirms(state) {
	return {
		...state,
		confirms: [],

	}
}

function handleActivityBanksSelected(state, action) {
	if (!state.canReplace && action.lesson.data.type === lessonTypes.MATCHING_PAIRS) {
		return {
			...state,
			confirms: [{
				type: confirmTypes.ACTIVITY_REPLACE,
				props: {
					lesson: action.lesson,
				},
			}],
		}
	} else {
		return state
	}
}

function handleActivityBanksSelectedConfirmed(state) {
	const newState = {
		...state,
		canReplace: true,
	}
	return clearConfirms(newState)
}

function getPairIndex(pairs, clientId) {
	return pairs.findIndex(item => item.clientId === clientId)

}

function getUpdatedPair(pairs, clientId) {
	const editingPairIndex = getPairIndex(pairs, clientId)
	return pairs[editingPairIndex]
}

function replacePairsElement(clientId, newItem, pairs) {
	const editingPairIndex = getPairIndex(pairs, clientId)
	return [...pairs.slice(0, editingPairIndex), newItem, ...pairs.slice(editingPairIndex + 1, pairs.length)]
}

function pairChangeText(state, action) {
	const text = action.value
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)

	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			value: text,
			touched: true,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function pairDiscardContent(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			type: '',
			value: null,
			touched: true,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function cardNewText(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			type: 'text',
			value: defaultTextValue,
			touched: true,
			error: null,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function createDefaultActivePair() {
	return {
		...defaultActivePair,
		clientId: makeUniqueId(),
		card1: {
			...defaultActiveCard,
		},
		card2: {
			...defaultActiveCard,
		},
	}
}

function removePair(clientId, pairs) {
	const pairIndex = getPairIndex(pairs, clientId)
	return [...pairs.slice(0, pairIndex), ...pairs.slice(pairIndex + 1, pairs.length)]
}

function pairLocallySaved(state, action) {
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	let pairs

	const cardValue = card => {
		if (editingPair[card].type === 'image') {
			return {
				...editingPair[card].value,
				icon: {
					...editingPair[card].value.icon,
					versions: {
						...editingPair[card].value.icon.versions,
						crop: null,
					},
				},
			}
		} else { // is text
			return editingPair[card].value
		}
	}

	const newPair = {
		...editingPair,
		deleted: false,
		card1: {
			id: editingPair.card1.id,
			type: editingPair.card1.type,
			value: cardValue('card1'),
		},
		card2: {
			id: editingPair.card2.id,
			type: editingPair.card2.type,
			value: cardValue('card2'),
		},
	}

	pairs = replacePairsElement(clientId, newPair, state.pairs)
	if (editingPair.parentId) {
		pairs = removePair(editingPair.parentId, pairs)
		editingPair.parentId = 0
	}

	return {
		...state,
		pairs,
	}
}

function pairLocallySavedAndCreateNew(state, action) {
	let newState = pairLocallySaved(state, action)
	newState = addNewPair(newState, action)
	return newState
}

function pairSaveSuccess(state, action) {
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)

	const newPair = {
		...editingPair,
		id: action.payload.id||editingPair.id,
	}

	const replaceCrop = card => {
		if (newPair[card].type === 'image') {
			newPair[card] = {
				...newPair[card],
				value: {
					...newPair[card].value,
					icon: {
						...newPair[card].value.icon,
						versions: {
							...newPair[card].value.icon.versions,
							crop: action.payload[card].value.icon.versions.crop,
						},
					},
				},
			}
		}
	}

	replaceCrop('card1')
	replaceCrop('card2')

	const pairs = replacePairsElement(clientId, newPair, state.pairs)

	return {
		...state,
		pairs,
		isModified: true,
		canReplace: false,
	}
}

function pairSaveValidateError(state, action) {
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		card1: {
			...editingPair.card1,
			touched: action.data.card1.valid,
		},
		card2: {
			...editingPair.card2,
			touched: action.data.card2.valid,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function imagePicked(state, action) {
	//TODO: Since this is using activePair instead of receiving the client id from the action, test if there can be an issue.
	const clientId = state.activePair
	const cardName = action.cardName
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			type: 'image',
			value: {
				...defaultImageValue,
				icon: {
					...defaultIcon,
				},
			},
			touched: true,
			error: null,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function imagePickSuccess(state, action) {
	const clientId = action.clientId
	const cardName = action.cardName
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			type: 'image',
			value: {
				...defaultImageValue,
				icon: {
					...editingPair[cardName].value.icon,
					id: action.icon.id,
					type: action.icon.type,
					versions: action.icon.versions,
					link: action.icon.link,
					status: action.icon.status,
					uploadId: action.uploadId				},
			},
			touched: true,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function pairAddAnotherAddSuccess(state, action) {
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		id: action.data.id,
		card1: {
			...defaultActiveCard,
			id: action.data.card1_id,
		},
		card2: {
			...defaultActiveCard,
			id: action.data.card2_id,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
		isModified: true,
	}
}

function addNewCardSuccess(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			id: action.id,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
		canReplace: false,
	}
}

function activityError(state) {
	return {
		...state,
		touched: false,
	}
}

function addNewPair(state) {
	const newPair = createDefaultActivePair()
	newPair.deleted = true
	newPair.parentId = 0
	return {
		...state,
		pairs: [...state.pairs, newPair],
		touched: true,
		activePair: newPair.clientId,
	}
}


function editPair(state, action) {
	const newPair = {
		...action.dataPair,
		deleted: true,
		clientId: makeUniqueId(),
		parentId: action.dataPair.clientId,
		card1: {
			...action.dataPair.card1,
			touched: true,
		},
		card2: {
			...action.dataPair.card2,
			touched: true,
		},
	}
	const parentIdIndex = getPairIndex(state.pairs, newPair.parentId)
	return {
		...state,
		pairs: [...state.pairs.slice(0, parentIdIndex), newPair, ...state.pairs.slice(parentIdIndex, state.pairs.length)],
		activePair: newPair.clientId,
	}
}

function deletePairConfirmed(state, action) {
	return {
		...state,
		isModified: true,
		pairs: state.pairs.filter(pair => pair.id !== action.id),
		activePair: '',
	}
}

function closeEditPair(state) {
	return {
		...state,
		activePair: '',
	}
}

function pairDiscardContentImageWithError(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			type: '',
			value: null,
			error: action.message,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),

	}
}

function pairAddAnImage(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			value: {
				icon: {
					...defaultIcon,
				},
			},
			touched: true,
			error: null,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),

	}
}

function uploaderValidateSuccess(state, action) {
	const clientId = action.clientId
	const cardName = action.cardName
	const editingPair = getUpdatedPair(state.pairs, clientId)
	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			value: {
				...editingPair[cardName].value,
				icon: {
					...editingPair[cardName].value.icon,
					uploadId: action.uploadId				},
			},
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function pairImageMatrixChange(state, action) {
	const newMatrix = action.value
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)

	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			value: {
				...editingPair[cardName].value,
				matrix: [...newMatrix],
			},
			touched: true,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

function pairImageLoad(state, action) {
	const cardName = action.cardName
	const clientId = action.clientId
	const editingPair = getUpdatedPair(state.pairs, clientId)

	const newItem = {
		...editingPair,
		[cardName]: {
			...editingPair[cardName],
			value: {
				...editingPair[cardName].value,
				originalSize: [action.width, action.height],
			},
			touched: true,
		},
	}
	return {
		...state,
		pairs: replacePairsElement(clientId, newItem, state.pairs),
	}
}

const matchingPairsCtReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.MATCHING_PAIRS_CT_INITIALIZE_ROUTE:
		case actionTypes.MATCHING_PAIRS_SCREENSHOT_INITIALIZE_ROUTE:
		case actionTypes.MATCHING_PAIRS_INITIALIZE_STANDALONE:
		case actionTypes.MATCHING_PAIRS_UPDATE_CREATION_TOOL:
			return initializeView(state, action)
		case actionTypes.MATCHING_PAIRS_CT_TOGGLE_HEADER:
			return toggleHeader(state, action)
		case actionTypes.MATCHING_PAIRS_CT_INSTRUCTIONS_CHANGE:
			return instructionsChange(state, action)
		case actionTypes.MATCHING_PAIRS_CT_INITIALIZE_SUCCESS:
			return initializeSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_SAVE_TIMER:
			return saveTimer(state, action)
		case actionTypes.MATCHING_PAIRS_CT_DELETE_TIMER:
			return deleteTimer(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_CHANGE_TEXT:
			return pairChangeText(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_DISCARD_CONTENT:
			return pairDiscardContent(state, action)
		case actionTypes.MATCHING_PAIRS_CT_CARD_NEW_TEXT:
			return cardNewText(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICKED:
			return imagePicked(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_SUCCESS:
			return imagePickSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_VALIDATE_ERROR:
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_UPLOAD_ERROR:
			return pairDiscardContentImageWithError(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_SUCCESS:
		case actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_SAVE_SUCCESS:
			return pairSaveSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_SUCCESS:
			return pairLocallySaved(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_VALIDATE_SUCCESS:
			return pairLocallySavedAndCreateNew(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_ERROR:
			return pairSaveValidateError(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_ADD_SUCCESS:
			return pairAddAnotherAddSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_ADD_NEW_CARD_SUCCESS:
			return addNewCardSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_VALIDATE_ERROR:
			return activityError(state, action)
		case actionTypes.MATCHING_PAIRS_CT_ADD_PAIR:
			return addNewPair(state, action)
		case actionTypes.MATCHING_PAIRS_CT_EDIT_PAIR:
			return editPair(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_DELETE_CONFIRMED:
			return deletePairConfirmed(state, action)
		case actionTypes.MATCHING_PAIRS_CT_CLOSE_EDIT_PAIR:
			return closeEditPair(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_AN_IMAGE:
			return pairAddAnImage(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_PICK_ERROR:
			return pairDiscardContentImageWithError(state, action)
		case actionTypes.MATCHING_PAIRS_CT_UPLOADER_VALIDATE_SUCCESS:
			return uploaderValidateSuccess(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_CHANGE_IMAGE_MATRIX:
			return pairImageMatrixChange(state, action)
		case actionTypes.MATCHING_PAIRS_CT_PAIR_IMAGE_LOAD:
			return pairImageLoad(state, action)
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED:
			return handleActivityBanksSelected(state, action)
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED_CONFIRMED:
			return handleActivityBanksSelectedConfirmed(state, action)
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED_CANCELLED:
			return clearConfirms(state, action)
		default:
			return state
	}
}

export default matchingPairsCtReducer

export {
	defaultState,
}
